import { AUTHZ_OBJECTS } from '../../../constants/authz/features.constants';

const MESSAGE_TEMPLATE_KEY = AUTHZ_OBJECTS.MESSAGE_TEMPLATE;
const PROFILE_KEY = AUTHZ_OBJECTS.PROFILE;
const BASE_USER_KEY = AUTHZ_OBJECTS.BASE_USER;

const VIEW_COUPLING_OBJECT_KEYS = new Set([
  MESSAGE_TEMPLATE_KEY,
  PROFILE_KEY,
  BASE_USER_KEY,
]);

const DEPENDENT_ACTIONS = ['can_create', 'can_update', 'can_delete'];
const CLEAR_ON_VIEW_REVOKE = [
  ...DEPENDENT_ACTIONS,
  'can_view_all',
  'can_update_all',
];

function applyViewCouplingRules(modelKey, action, granted, policy) {
  if (!VIEW_COUPLING_OBJECT_KEYS.has(modelKey)) {
    return policy;
  }

  if (granted && DEPENDENT_ACTIONS.includes(action)) {
    policy.can_view = true;
  }

  if (!granted && action === 'can_view') {
    CLEAR_ON_VIEW_REVOKE.forEach((dependentAction) => {
      policy[dependentAction] = false;
    });
  }

  return policy;
}

/**
 * Enforces implicit object-policy dependencies when editing profile grants.
 * Create/edit/delete require view; revoking view clears other actions.
 *
 * @param {string} model
 * @param {string} action
 * @param {boolean} granted
 * @param {object} grants
 */
export function applyObjectGrantSideEffects(model, action, granted, grants) {
  const modelKey = (model || '').toLowerCase();
  const objects = { ...(grants.objects || {}) };
  const policy = applyViewCouplingRules(
    modelKey,
    action,
    granted,
    {
      ...(objects[modelKey] || {}),
      [action]: Boolean(granted),
    }
  );

  objects[modelKey] = policy;

  return {
    ...grants,
    objects,
  };
}

function normalizeObjectPolicy(modelKey, grants) {
  const policy = grants.objects?.[modelKey];
  if (!policy) {
    return grants;
  }

  let next = grants;

  if (DEPENDENT_ACTIONS.some((action) => policy[action])) {
    next = applyObjectGrantSideEffects(modelKey, 'can_view', true, next);
  }

  const normalizedPolicy = next.objects[modelKey] || {};
  if (!normalizedPolicy.can_view) {
    CLEAR_ON_VIEW_REVOKE.forEach((action) => {
      if (normalizedPolicy[action]) {
        next = applyObjectGrantSideEffects(modelKey, action, false, next);
      }
    });
  }

  return next;
}

/**
 * @param {object} grants
 */
export function normalizeObjectGrantSideEffects(grants) {
  if (!grants?.objects) {
    return grants;
  }

  return [...VIEW_COUPLING_OBJECT_KEYS].reduce(
    (next, modelKey) => normalizeObjectPolicy(modelKey, next),
    grants
  );
}
